import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { type CartLine, cartKey, useCartStore } from '../../lib/cartStore.js';

import type { Saving } from './discountDisplay.js';

/**
 * What the shopper was shown when a line went into the cart: the price they
 * accepted AND the pre-discount price that was struck through beside it.
 *
 * The cart itself carries only the charged price (it is display-only — the
 * server re-resolves every price at checkout), and drawing a strikethrough must
 * not cost a product lookup per line. So each add-to-cart records its own saving
 * here, keyed exactly like the cart line it belongs to.
 *
 * A record is honoured only while it still describes that line: if the line was
 * re-priced afterwards (signing in moves the shopper onto a customer group — see
 * cartPricing.ts) the stored price no longer matches and nothing is shown. A
 * stale "was 10,000" is worse than no strikethrough at all.
 */
interface SavingRecord extends Saving {
  /** The line price this saving was recorded against. */
  price: string;
}

interface SavingsState {
  /** The store these records belong to; another slug starts a fresh cart. */
  slug: string | null;
  byKey: Record<string, SavingRecord>;
  remember(slug: string, key: string, rec: SavingRecord | null, keep: Set<string>): void;
}

const useSavingsStore = create<SavingsState>()(
  persist(
    (set) => ({
      slug: null,
      byKey: {},
      remember: (slug, key, rec, keep) =>
        set((state) => {
          const prior = state.slug === slug ? state.byKey : {};
          // Only keep records for lines that are still in the cart, so the map
          // can't grow forever across months of shopping.
          const byKey: Record<string, SavingRecord> = {};
          for (const [k, v] of Object.entries(prior)) if (keep.has(k)) byKey[k] = v;
          if (rec) byKey[key] = rec;
          else delete byKey[key];
          return { slug, byKey };
        }),
    }),
    // localStorage, like the cart itself — a reloaded /cart keeps its savings.
    { name: 'erp-store-cart-savings', storage: createJSONStorage(() => localStorage) },
  ),
);

/** The cart-line identity a saving is filed under (same key the cart uses). */
type LineRef = Pick<CartLine, 'itemId' | 'itemUnitId' | 'itemVariantId' | 'price'>;

/**
 * Record (or clear, with `saving = null`) what the shopper saved on a line.
 * Call it right after cartStore.add so the live cart — which decides which
 * records survive the prune — already contains the line.
 */
export function rememberSaving(slug: string, line: LineRef, saving: Saving | null): void {
  const key = cartKey(line);
  const cart = useCartStore.getState();
  const keep = new Set(cart.slug === slug ? cart.items.map((l) => cartKey(l)) : []);
  keep.add(key);
  useSavingsStore.getState().remember(slug, key, saving ? { ...saving, price: line.price } : null, keep);
}

/**
 * Look up the saving to show for a cart line. Returns a plain function rather
 * than a derived map because zustand selectors must not allocate (a fresh object
 * every render re-renders forever under React 19's useSyncExternalStore).
 */
export function useCartSavings(slug: string): (line: CartLine) => Saving | null {
  const recordedSlug = useSavingsStore((s) => s.slug);
  const byKey = useSavingsStore((s) => s.byKey);
  return (line) => {
    if (recordedSlug !== slug) return null;
    const rec = byKey[cartKey(line)];
    // The line must still be at the price the saving was recorded against.
    if (!rec || Number(rec.price) !== Number(line.price)) return null;
    return { originalPrice: rec.originalPrice, amount: rec.amount, percent: rec.percent };
  };
}

/** Total saved across the visible lines (quantity included), in the cart currency. */
export function totalSaving(lines: CartLine[], savingOf: (line: CartLine) => Saving | null): number {
  return lines.reduce((sum, l) => {
    const s = savingOf(l);
    return s ? sum + Number(s.amount) * l.quantity : sum;
  }, 0);
}
